import React, { useMemo } from 'react';

import moment from 'moment';
import { useIntl } from 'react-intl';

import DisplayDuration from '@src/components/DisplayDuration';
import type { ResponsiveTableColumn } from '@src/components/Table';
import TableProgress from '@src/components/Table/components/TableProgress';
import SupervisorClosureTag from '@src/packages/workOrders/components/SupervisorClosureTag';

import type {
  OrderDrilldownRow,
  UserDrilldownRow,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';

/** Table columns of the drill-down drawer, for order lists and user lists. */
export const useTasksDrilldownColumns = () => {
  const intl = useIntl();

  return useMemo(() => {
    const timeLocale = intl.locale?.startsWith('en') ? 'en' : 'es';

    const orderColumns: ResponsiveTableColumn<OrderDrilldownRow>[] = [
      {
        key: 'task-name',
        title: intl.formatMessage({ id: 'schedules.type.task' }),
        dataIndex: 'name',
        ellipsis: true,
      },
      {
        key: 'task-division',
        title: intl.formatMessage({ id: 'schedules.list.division.label' }),
        render: order => order.divisionName || '-',
        ellipsis: true,
      },
      {
        key: 'task-template',
        title: intl.formatMessage({ id: 'work.order.info.template' }),
        ellipsis: true,
        render: order => order.templateName ?? '-',
      },
      {
        key: 'task-duration',
        title: intl.formatMessage({ id: 'schedules.list.duration.label' }),
        align: 'center',
        render: order => <DisplayDuration minutes={order.plannedDurationMinutes} />,
      },
      {
        key: 'task-time',
        title: intl.formatMessage({ id: `schedules.list.time.${timeLocale}.label` }),
        align: 'center',
        render: order => intl.formatMessage(
          { id: `schedules.list.time.${timeLocale}.value` },
          { startDate: moment(order.startAt).toDate(), endDate: moment(order.endAt).toDate() },
        ),
      },
      {
        key: 'task-users',
        title: intl.formatMessage({ id: 'schedules.list.users.label' }),
        align: 'center',
        render: order => order.assignees.map(user => user.name).join(', ') || '-',
      },
      {
        key: 'task-events',
        title: intl.formatMessage({ id: 'schedules.list.total.events' }),
        align: 'center',
        render: order => `${order.finishedEvents}/${order.expectedEvents}`,
      },
      {
        key: 'task-compliance',
        title: intl.formatMessage({ id: 'schedules.list.progress.label' }),
        align: 'center',
        render: order => (
          <>
            <TableProgress percentage={order.completionPercent} />
            {order.outcome === 'supervisor_closed' && <SupervisorClosureTag />}
          </>
        ),
      },
    ];

    const userColumns: ResponsiveTableColumn<UserDrilldownRow>[] = [
      {
        key: 'user-name',
        title: intl.formatMessage({ id: 'dashboards.schedules.summary.users.name' }),
        dataIndex: 'name',
      },
      {
        key: 'user-rut',
        title: intl.formatMessage({ id: 'dashboards.schedules.summary.users.rut' }),
        render: user => user.rut ?? '-',
      },
      {
        key: 'user-orders',
        title: intl.formatMessage({ id: 'dashboards.tasks.summary.metric.orders' }),
        dataIndex: 'assigned',
        align: 'right',
      },
      {
        key: 'user-completed',
        title: intl.formatMessage({ id: 'dashboards.tasks.summary.metric.completed' }),
        dataIndex: 'completed',
        align: 'right',
      },
      {
        key: 'user-progress',
        title: intl.formatMessage({ id: 'schedules.list.progress.label' }),
        align: 'right',
        render: user => `${Math.round(user.completionPercent)}%`,
      },
    ];

    return { orderColumns, userColumns };
  }, [intl]);
};
